import {source} from "azure-maps-control";
import { TerritoryOutlinesWorkerBroker } from "./TerritoryOutlinesWorkerBroker";

import type TerritoryModel from "@esWebAPI/es/map/TerritoryModel";
import type {ITerritoryActions, ITerritoryFeature} from "@esWebAPI/es/map/TerritoryModel";
import type { Feature } from "geojson";
import type {ILayerView, LayerInfo} from "@esWebAPI/es/model/Layer";
import type {Map, data} from "azure-maps-control";
import type { TerritoryBaseLayer } from "./TerritoryBaseLayer";
import type {ITerritoryBaseLayer, TerritoryBaseFeatureWorker} from "@esWebAPI/es/map/layer/territory/TerritoryLayer";
import type {IEventChannel} from "@esWebAPI/es/model/Models";
import {DatasourceManager} from "@esWebAPI/es/map/module/DatasourceManager";
import {AzurePolygonLayer} from "@esWebAPI/es/map/layer/azure/AzurePolygonLayer";
import {ThematicStylerFactory} from "@esWebAPI/es/map/styler/ThematicStylerFactory";
import {ExpressionFilterSpecification} from "@maplibre/maplibre-gl-style-spec";

export class TerritoryOutlinesLayer implements ITerritoryBaseLayer {

    private readonly datasourceManager: DatasourceManager;
    private readonly _territoryModel: TerritoryModel
    private readonly _eventChannel: IEventChannel;
    private readonly outlinesWorkerBroker: TerritoryOutlinesWorkerBroker;
    private readonly _layerView: ILayerView;
    private readonly _layerInfo: LayerInfo;

    private readonly baseLayer: TerritoryBaseLayer;

    private _polygonLayer: AzurePolygonLayer;
    private datasource: source.DataSource|undefined;

    loadComplete : JQuery.Deferred<void>;
    loadingFinish: boolean;
    isUpdating: boolean;
    updateComplete : any;
    queue: any[] = []


    constructor(baseMap: Map, datasourceManager: DatasourceManager,
                territoryModel:TerritoryModel,
                baseLayer: TerritoryBaseLayer,
                stylerFactory: ThematicStylerFactory,
                eventChannel: IEventChannel) {


        this.datasourceManager = datasourceManager;
        this._territoryModel = territoryModel;
        this._eventChannel = eventChannel;

        this.baseLayer = baseLayer;

        this.loadComplete = $.Deferred();
        this.loadingFinish = false;

        this.isUpdating = false;
        this.updateComplete = $.Deferred();

        this._layerView = territoryModel.getManageTerritoryMainLayerView().getLayerView();
        this._layerInfo = territoryModel.getManageTerritoryMainLayerView().getLayerInfo();

        // Working for saving, updating outlines. Outlines sorted in lfstore.
        this.outlinesWorkerBroker = new TerritoryOutlinesWorkerBroker(this.getLayerView().getLayerMetaId());

        eventChannel.addListener("onTerritoryChange", this.onTerritoryChange, this);
        eventChannel.addListener("onTerritoryLevelChange", this.onTerritoryLevelChange, this);

        const level  = this._territoryModel.getCurrentLevel() as number + 1;
        const activeIds = this._territoryModel.getTerritoryFeaturesByLevelObjRef(level).map(t => t.ES_ID);
        const filter = ['in', ['id'], ['literal', activeIds]] as ExpressionFilterSpecification;

        this._polygonLayer = new AzurePolygonLayer(baseMap, this._layerView, datasourceManager,
            stylerFactory.getColorStyler(this._layerView), filter);

        this.createLayers().then(() => {
            this.loadComplete.resolve();
        });
    }

    private async createLayers() {

        this.datasource = this.datasourceManager.createBlank(this._layerView, this._layerInfo, 'es_outline_' + this._layerView.id);

        await this._polygonLayer.create(this.datasource);
        if (!this._territoryModel.isHierarchical() || this._territoryModel.isLowestLevel()) {
            this._polygonLayer.hide();
        } else {
            this._polygonLayer.show();
        }

        await this.baseLayer.onReady;
        this._eventChannel.fireEvent("onTerritoryGenerateOutlinesStart");
        const lastModified = this.getLayerView().getFeaturesModified();
        await this.outlinesWorkerBroker.init(this.baseLayer, this._territoryModel.getTerritoryFeaturesObjRef(), this._territoryModel.territoryMeta.levels, lastModified);
        this._eventChannel.fireEvent("onTerritoryGenerateOutlinesFinish");

        const featureCollection = await this.outlinesWorkerBroker.getAll();
        this.datasource.add(featureCollection as data.FeatureCollection);
    }

    getLayerView () {
        return this._territoryModel.getManageTerritoryMainLayerView();
    }

    isOutlinesLayer () {
        return true;
    }

    isBaseLayer () {
        return false;
    }

    isRelatedExtraPointLayer () {
        return false;
    }

    isRelatedLayer () {
        return false;
    }

    isVisibleRes() {
        return true;
    }

    public get featureWorker() {
        return this.outlinesWorkerBroker as TerritoryBaseFeatureWorker;
    }

    get territoryModel () {
        return this._territoryModel;
    }

    clear () {
        console.log("[TOL] Clear");
        return this.outlinesWorkerBroker.clear(this.getLayerView().getLayerMetaId());
    }

    saveState(stateId: number) {
        return this.outlinesWorkerBroker.saveState(stateId);
    }

    async loadState(stateId: number) {
        await this.outlinesWorkerBroker.loadState(stateId);
        const featureCollection = await this.outlinesWorkerBroker.getAll();
        this.datasource?.clear();
        this.datasource?.add(featureCollection as data.FeatureCollection);
        this._eventChannel.fireEvent("onTerritoryOutlinesLayerUpdate");
    }

    getVisualCentersInBounds(bounds: [number, number, number, number], level: number){
        return this.outlinesWorkerBroker.getVisualCentersInBounds(bounds, level);
    }

    getVisualCenterFeatures(bounds: [number, number, number, number],level: number | undefined,  callback: (features: Feature[]) => void) {
        return this.outlinesWorkerBroker.getVisualCenterFeatures(bounds, level, callback);
    }

    private onTerritoryChange(actions: ITerritoryActions, territories: ITerritoryFeature[], affectedParentsIds: number[]) {

        const changedTerritoriesIds = this.territoryModel.getChangedTerritoriesIds()
        this.queue.push({actions, territories, affectedParentsIds, changedTerritoriesIds});

        const processQueue = () => {
            if(this.queue.length > 0) {
                const op = this.queue.shift();
                const timerName = Object.keys(actions)[0];
                console.time(timerName)
                this.updateOutlines(op.actions, op.territories, op.affectedParentsIds, op.changedTerritoriesIds).then(() => {
                    console.timeEnd(timerName);
                    this._eventChannel.fireEvent("onTerritoryOutlinesLayerUpdate");
                    processQueue();
                });
            } else {
                this.isUpdating = false;
                this.updateComplete.resolve();

                this.outlinesWorkerBroker.getAll().then(featureCollection => {
                    this.datasource?.clear();
                    this.datasource?.add(featureCollection as data.FeatureCollection);
                });
            }
        }

        if(!this.isUpdating){
            this.isUpdating = true;
            this.updateComplete = $.Deferred();
            processQueue();
        }
    }

    private async onTerritoryLevelChange() {
        const activeIds = this._territoryModel.getTerritoryFeaturesByLevelObjRef(this._territoryModel.getCurrentLevel() as number + 1).map(t => t.ES_ID);
        const visible = this._territoryModel.isHierarchical() && !this._territoryModel.isLowestLevel();
        const filter = ['in', ['id'], ['literal', activeIds]] as ExpressionFilterSpecification;
        this._polygonLayer.setLayerFilter(filter);
        await this._polygonLayer.update(this._layerView);
        if (!visible) {
            this._polygonLayer.hide();
        } else {
            this._polygonLayer.show();
        }

        this._eventChannel.fireEvent("onTerritoryOutlinesLayerUpdate");
    }

    private updateOutlines(actions: ITerritoryActions, territories: ITerritoryFeature[], affectedParentsIds: number[], changedTerritoriesIds: number[]) {
        if( (!actions["ADD_FEATS"] || !actions["ADD_FEATS"].baseFeatureIds || actions["ADD_FEATS"].baseFeatureIds.length === 0) &&
            (!actions["REMOVE_FEATS"] || !actions["REMOVE_FEATS"].baseFeatureIds || actions["REMOVE_FEATS"].baseFeatureIds.length === 0) &&
            !actions["RELOAD"] && !actions["REMOVE_TERRITORY"] && !actions["MOVE"] ) {
            // No need to update the outline for any of these action.
            return es.util.deferred.resolved();
        }

       return this.outlinesWorkerBroker.updateOutlines(actions, territories, affectedParentsIds, changedTerritoriesIds, this.territoryModel.getTerritoryFeaturesObjRef());
    }

    get layerView() {
        return this._layerView;
    }

    createLabel(feature: any): string {
        return this.territoryModel.get(feature.id as number).NAME;
    }

    getLayerIds(): string[] {
        return [this._polygonLayer.getLayerId()];
    }

    public get onReady(): JQuery.Deferred<void> {
        return this.loadComplete;
    }

    public hide() {}

    public show() {}

    public update(): Promise<void> {
        return Promise.resolve();
    }

    public destroy() {
        this._polygonLayer.remove();
        if (this.datasource) {
            this.datasource.clear();
            this.datasourceManager.remove(this.datasource.getId())
        }
        this.outlinesWorkerBroker.destroy();
    }
}
